<script setup>
// Shell premium e genérico para telas de settings.
// Reutilizado pela Auditoria, Editar Agente e demais telas de configurações.
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';

defineProps({
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isEmpty: {
    type: Boolean,
    default: false,
  },
  isError: {
    type: Boolean,
    default: false,
  },
  backUrl: {
    type: [String, Object],
    default: '',
  },
  showPagination: {
    type: Boolean,
    default: false,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  totalCount: {
    type: Number,
    default: 0,
  },
  itemsPerPage: {
    type: Number,
    default: 25,
  },
});

const emit = defineEmits(['update:currentPage', 'retry']);

const { t } = useI18n();

const handlePageChange = page => {
  emit('update:currentPage', page);
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1">
    <header class="sticky top-0 z-10 px-6">
      <div class="w-full max-w-5xl mx-auto">
        <div
          class="flex items-start lg:items-center justify-between w-full py-6 lg:py-0 lg:h-20 gap-4 lg:gap-2 flex-col lg:flex-row"
        >
          <div class="flex gap-3 items-center">
            <BackButton v-if="backUrl" :back-url="backUrl" />
            <div class="flex flex-col">
              <span class="text-xl font-medium text-n-slate-12">
                {{ title }}
              </span>
              <p
                v-if="description"
                class="text-body-small text-n-slate-11 mt-0.5"
              >
                {{ description }}
              </p>
            </div>
          </div>
          <div class="flex gap-2">
            <slot name="actions" />
          </div>
        </div>
        <slot name="subHeader" />
      </div>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <div class="w-full max-w-5xl h-full mx-auto py-4">
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-10 text-n-slate-11"
        >
          <Spinner />
        </div>
        <template v-else-if="isError">
          <slot name="errorState">
            <div
              class="rounded-xl border border-n-weak p-6 flex flex-col items-center gap-3 text-center"
            >
              <i class="i-lucide-alert-triangle size-8 text-n-ruby-11" />
              <p class="text-body-small text-n-slate-11">
                {{ t('GENERAL.ERROR_STATE.MESSAGE') }}
              </p>
              <Button
                :label="t('GENERAL.ERROR_STATE.RETRY')"
                faded
                slate
                icon="i-lucide-refresh-cw"
                @click="emit('retry')"
              />
            </div>
          </slot>
        </template>
        <div v-else-if="isEmpty">
          <slot name="emptyState" />
        </div>
        <template v-else>
          <slot name="body" />
          <slot />
        </template>
      </div>
    </main>
    <footer v-if="showPagination" class="sticky bottom-0 z-10">
      <PaginationFooter
        :current-page="currentPage"
        :total-items="totalCount"
        :items-per-page="itemsPerPage"
        class="max-w-[67rem]"
        @update:current-page="handlePageChange"
      />
    </footer>
  </section>
</template>
